import { lazy } from 'react';

import { lazyWithRetry } from '@router/lazyRetry';
import {
  ADDITIONAL_CREDITS,
  CALLBACK,
  CALLBACK_MOCK,
  CARD_SELECT,
  COUPONS,
  NEXT_COUPONS,
  EVALUATIONS,
  NEXT_EVALUATIONS,
  FORCE_ERROR,
  LOGOUT,
  PRODUCT_DETAIL,
  PRODUCTS_LIST,
  PROFILE,
  PROFILE_STEPS,
  PROFILE_SUCCESS,
  NEXT_PROFILE_FIRST,
  NEXT_PROFILE_SUCCESS,
  PROMOTION,
  PROMOTIONS,
  PROMOTIONS_NEXT,
  REGISTER,
  ROUTER,
  ROUTER_POST,
  ROUTER_NEXT_PRE,
  ROUTER_NEXT_POST,
  SHOP_SELECT,
  SHOPS_PARTICIPANTS,
  SHUFFLE,
  START,
  UNSUB,
  EXTRA,
  LANDING_PAGE,
  STATIC_CONTENT_REMOTE,
  MOBILE_CLEAN,
  COOKIES_POLICY,
  EXTERNAL_SURVEY,
  PROMOTION_NEXT,
  LOGIN_SELECT,
  MOCK_LANDING_CAMPAIGN_PAGE,
  LANDING_CAMPAIGN_PAGE,
  PO30,
  CONTEST_COMMUNITY,
} from '@utils/router';
import { isProd } from '@config/getEnviroment';

const Root = lazy(() => lazyWithRetry(() => import(/* webpackChunkName: "Root" */ '@layouts/Root'), 'Root'));

const LegacyNotFound = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "NotFound" */ '@pages/LegacyNotFound'), 'LegacyNotFound')
);
const MainLayout = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "MainLayout" */ '@layouts/MainLayout'), 'MainLayout')
);
const SsoLayout = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "SsoLayout" */ '@layouts/SsoLayout'), 'SsoLayout')
);
const MiddleLayout = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "MiddleLayout" */ '@layouts/MiddleLayout'), 'MiddleLayout')
);

const CleanLayout = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "CleanLayout" */ '@layouts/CleanLayout'), 'CleanLayout')
);

const LegacyProfilePage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyProfilePage" */ '@pages/LegacyProfile'), 'LegacyProfile')
);
const ProfileStepsPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyProfileFirstPage" */ '@pages/ProfileSteps'), 'ProfileSteps')
);
const LegacyProfileSuccessPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyProfileSuccessPage" */ '@pages/LegacyProfileSuccess'),
    'LegacyProfileSuccess'
  )
);
const NextProfileFirstPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "NextProfileFirstPage" */ '@pages/NextProfileFirst'),
    'NextProfileFirst'
  )
);
const NextProfileSuccessPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "NextProfileSuccessPage" */ '@pages/NextProfileSuccess'),
    'NextProfileSuccess'
  )
);
const Home = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "HomePage" */ '@pages/LegacyPromotions'), 'HomePage')
);
const LegacyPromotionsPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyPromotionsPage" */ '@pages/LegacyPromotions'),
    'LegacyPromotions'
  )
);
const NextPromotionsPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "NextPromotionsPage" */ '@pages/NextPromotions'), 'NextPromotions')
);
const LegacyPromotionPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyPromotion" */ '@pages/LegacyPromotion'), 'LegacyPromotion')
);
const NextPromotionPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "NextPromotionPage" */ '@pages/NextPromotion'), 'NextPromotion')
);
const NextRouterPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "NextRouterPage" */ '@pages/NextRouter'), 'NextRouter')
);
const LegacyCardsPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyCardSelection" */ '@pages/LegacyCardSelection'),
    'LegacyCardSelection'
  )
);
const LegacyIframe = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyIframe" */ '@pages/LegacyIframe'), 'LegacyIframe')
);
const LegacyIframePost = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyIframePost" */ '@pages/LegacyIframePost'), 'LegacyIframePost')
);
const LegacyExtra = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyExtra" */ '@pages/LegacyExtra'), 'LegacyExtra')
);
const LegacyExternalSurvey = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyExternalSurvey" */ '@pages/LegacySurvey'),
    'LegacyExternalSurvey'
  )
);
const LegacyCouponsPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyCouponsPage" */ '@pages/LegacyCoupons'), 'LegacyCoupons')
);
const NextCouponsPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "NextCouponsPage" */ '@pages/NextCoupons'), 'NextCoupons')
);
const LegacyEvaluationsPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyEvaluationsPage" */ '@pages/LegacyEvaluations'),
    'LegacyEvaluations'
  )
);
const NextEvaluationsPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "NextEvaluationsPage" */ '@pages/NextEvaluations'), 'NextEvaluations')
);
const LegacyCallbackPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyCallbackPage" */ '@pages/LegacyCallback'), 'LegacyCallback')
);
const MockCallbackPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyCallbackPage" */ '@pages/MockCallback'), 'MockCallback')
);
const ShopSelectPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyShopSelect" */ '@pages/ShopSelect'), 'ShopSelect')
);
const LegacyRegisterPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyRegisterPage" */ '@pages/LegacyRegister'), 'LegacyRegister')
);
const LegacyStaticContent = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyStaticContent" */ '@pages/LegacyStaticContent'),
    'LegacyStaticContent'
  )
);
const StaticRemoteContent = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "StaticRemoteContent" */ '@components/StaticGenerator/Remote'),
    'StaticRemoteContent'
  )
);
const LegacyStartPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyStartPage" */ '@pages/LegacyStart'), 'LegacyStart')
);
const MobilePage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "MobilePage" */ '@pages/Mobile'), 'MobilePage')
);
const LegacyLogoutPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyLogoutPage" */ '@pages/LegacyLogout'), 'LegacyLogout')
);

const LegacyProductsListPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyProductsListPage" */ '@pages/LegacyProductsList'),
    'LegacyProductsList'
  )
);
const LegacyProductDetailPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LegacyProductDetailPage" */ '@pages/LegacyProductDetail'),
    'LegacyProductDetail'
  )
);

const LegacyShufflePage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LegacyShufflePage" */ '@pages/Shuffle'), 'LegacyShuffle')
);
const AdditionalCreditsPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "AdditionalCreditsPage" */ '@pages/AdditionalCredits'),
    'AdditionalCredits'
  )
);
const ForceErrorPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "ForceErrorPage" */ '@pages/ForceError'), 'ForceError')
);
const UnsubPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "UnsubPage" */ '@pages/LegacyUnsub'), 'Unsub')
);
const LoginSelectPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LoginSelectPage" */ '@pages/LoginSelect'), 'LoginSelect')
);
const LandingCampaignPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "LandingCampaignPage" */ '@pages/LandingCampaign'), 'LandingCampaign')
);
const LandingCampaignMockPage = lazy(() =>
  lazyWithRetry(
    () => import(/* webpackChunkName: "LandingCampaignMockPage" */ '@pages/LandingCampaignMock'),
    'LandingCampaignMock'
  )
);
const RatingAndReviewPage = lazy(() =>
  lazyWithRetry(() => import(/* webpackChunkName: "RatingAndReviewPage" */ '@pages/RatingAndReview'), 'RatingAndReview')
);

const routes = [
  {
    component: Root,
    routes: [
      {
        path: [
          '/',
          PROMOTIONS,
          PROMOTIONS_NEXT,
          COUPONS,
          NEXT_COUPONS,
          EVALUATIONS,
          NEXT_EVALUATIONS,
          PROMOTION,
          PROMOTION_NEXT,
          PROFILE,
          PROFILE_STEPS,
          PROFILE_SUCCESS,
          NEXT_PROFILE_FIRST,
          NEXT_PROFILE_SUCCESS,
          CARD_SELECT,
          ROUTER,
          ROUTER_POST,
          ROUTER_NEXT_PRE,
          ROUTER_NEXT_POST,
          EXTRA,
          SHOP_SELECT,
          SHUFFLE,
          ADDITIONAL_CREDITS,
          UNSUB,
          PO30,
          CONTEST_COMMUNITY,
        ],
        exact: true,
        component: SsoLayout,
        routes: [
          {
            path: '/',
            exact: true,
            component: Home,
          },
          {
            path: PROMOTION,
            exact: true,
            component: LegacyPromotionPage,
          },
          {
            path: PROMOTIONS,
            exact: true,
            component: LegacyPromotionsPage,
          },
          {
            path: PROMOTIONS_NEXT,
            exact: true,
            component: NextPromotionsPage,
          },
          {
            path: PROMOTION_NEXT,
            exact: true,
            component: NextPromotionPage,
          },
          {
            path: COUPONS,
            exact: true,
            component: LegacyCouponsPage,
          },
          {
            path: NEXT_COUPONS,
            exact: true,
            component: NextCouponsPage,
          },
          {
            path: EVALUATIONS,
            exact: true,
            component: LegacyEvaluationsPage,
          },
          {
            path: NEXT_EVALUATIONS,
            exact: true,
            component: NextEvaluationsPage,
          },
          {
            path: PROFILE,
            exact: true,
            component: LegacyProfilePage,
          },
          {
            path: PROFILE_STEPS,
            exact: true,
            component: ProfileStepsPage,
          },
          {
            path: PROFILE_SUCCESS,
            exact: true,
            component: LegacyProfileSuccessPage,
          },
          {
            path: NEXT_PROFILE_FIRST,
            exact: true,
            component: NextProfileFirstPage,
          },
          {
            path: NEXT_PROFILE_SUCCESS,
            exact: true,
            component: NextProfileSuccessPage,
          },
          {
            path: CARD_SELECT,
            exact: true,
            component: LegacyCardsPage,
          },
          {
            path: ROUTER,
            exact: true,
            component: LegacyIframe,
          },
          {
            path: ROUTER_POST,
            exact: true,
            component: LegacyIframePost,
          },
          {
            path: ROUTER_NEXT_PRE,
            exact: true,
            component: NextRouterPage,
          },
          {
            path: ROUTER_NEXT_POST,
            exact: true,
            component: NextRouterPage,
          },
          {
            path: EXTRA,
            exact: true,
            component: LegacyExtra,
          },
          {
            path: SHOP_SELECT,
            exact: true,
            component: ShopSelectPage,
            withoutFooter: true,
          },
          {
            path: SHUFFLE,
            exact: true,
            component: LegacyShufflePage,
          },
          {
            path: ADDITIONAL_CREDITS,
            exact: true,
            component: AdditionalCreditsPage,
          },
          {
            path: UNSUB,
            exact: true,
            component: UnsubPage,
          },
          {
            path: PO30,
            exact: true,
            component: RatingAndReviewPage,
          },
          {
            path: CONTEST_COMMUNITY,
            exact: true,
            component: LegacyStaticContent,
          },
          {
            path: '*',
            exact: true,
            component: LegacyNotFound,
          },
        ],
      },
      {
        path: [
          CALLBACK,
          ...(!isProd() ? [CALLBACK_MOCK] : []),
          PRODUCTS_LIST,
          PRODUCT_DETAIL,
          LANDING_PAGE,
          SHOPS_PARTICIPANTS,
          COOKIES_POLICY,
          LOGOUT,
          FORCE_ERROR,
          STATIC_CONTENT_REMOTE,
        ],
        component: MainLayout,
        routes: [
          {
            path: CALLBACK,
            exact: true,
            component: LegacyCallbackPage,
            withoutFooter: true,
          },
          ...(!isProd()
            ? [
                {
                  path: CALLBACK_MOCK,
                  exact: true,
                  component: MockCallbackPage,
                  withoutFooter: true,
                },
              ]
            : []),
          {
            path: PRODUCTS_LIST,
            exact: true,
            component: LegacyProductsListPage,
          },
          {
            path: PRODUCT_DETAIL,
            exact: true,
            component: LegacyProductDetailPage,
          },
          {
            path: SHOPS_PARTICIPANTS,
            exact: true,
            component: LegacyStaticContent,
          },
          {
            path: COOKIES_POLICY,
            exact: true,
            component: LegacyStaticContent,
          },
          {
            path: LANDING_PAGE,
            exact: true,
            component: LegacyStaticContent,
          },
          {
            path: LOGOUT,
            exact: true,
            component: LegacyLogoutPage,
          },
          {
            path: FORCE_ERROR,
            exact: true,
            component: ForceErrorPage,
          },
          {
            path: STATIC_CONTENT_REMOTE,
            exact: true,
            component: StaticRemoteContent,
          },
          {
            path: '*',
            component: LegacyNotFound,
          },
        ],
      },
      {
        path: [REGISTER, EXTERNAL_SURVEY],
        component: MiddleLayout,
        routes: [
          {
            path: REGISTER,
            exact: true,
            component: LegacyRegisterPage,
            withoutFooter: true,
          },
          {
            path: EXTERNAL_SURVEY,
            exact: true,
            component: LegacyExternalSurvey,
            withoutFooter: true,
          },
          {
            path: '*',
            component: LegacyNotFound,
          },
        ],
      },
      {
        path: !isProd()
          ? [START, MOBILE_CLEAN, LANDING_CAMPAIGN_PAGE, LOGIN_SELECT, MOCK_LANDING_CAMPAIGN_PAGE]
          : [START, MOBILE_CLEAN, LANDING_CAMPAIGN_PAGE],
        component: CleanLayout,
        routes: [
          {
            path: START,
            exact: true,
            component: LegacyStartPage,
            withoutFooter: true,
          },
          {
            path: LANDING_CAMPAIGN_PAGE,
            exact: true,
            component: LandingCampaignPage,
            withoutFooter: true,
          },
          {
            path: MOBILE_CLEAN,
            exact: true,
            component: MobilePage,
            withoutFooter: true,
          },
          ...(!isProd()
            ? [
                {
                  path: LOGIN_SELECT,
                  exact: true,
                  component: LoginSelectPage,
                  withoutFooter: true,
                },
                {
                  path: MOCK_LANDING_CAMPAIGN_PAGE,
                  exact: true,
                  component: LandingCampaignMockPage,
                  withoutFooter: true,
                },
              ]
            : []),
        ],
      },
      {
        path: '*',
        component: MainLayout,
        routes: [
          {
            path: '*',
            component: LegacyNotFound,
          },
        ],
      },
    ],
  },
];

export { routes };
